import {
  FileText,
  Import,
  Settings,
  User,
  Users,
  Sliders,
  LayoutDashboard,
  Contact,
  Building2,
  TrendingUp,
  Target,
  Calendar,
  Zap,
  BookOpen,
  PhoneCall,
  LogOut,
  Workflow,
} from "lucide-react";
import { CanAccess, useTranslate, useUserMenu } from "ra-core";
import { Link, matchPath, useLocation } from "react-router";
import { RefreshButton } from "@/components/admin/refresh-button";
import { ThemeModeToggle } from "@/components/admin/theme-mode-toggle";
import { UserMenu } from "@/components/admin/user-menu";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { ImportDataPage } from "../import/ImportDataPage";
import { ChangelogPage } from "../misc/ChangelogPage";
import { NotificationsBell } from "./NotificationsBell";
import { useVoip } from "../voip/VoipContext";
import { useCRM } from "../context/CRMContext";
import { useSidebarCollapse } from "./SidebarContext";

const Header = () => {
  const { darkModeLogo, lightModeLogo, title } = useConfigurationContext();
  const location = useLocation();
  const translate = useTranslate();
  const { isCollapsed, toggleSidebar } = useSidebarCollapse();

  let currentPath: string | boolean = "/";
  if (matchPath("/", location.pathname)) {
    currentPath = "/";
  } else if (matchPath("/contacts/*", location.pathname)) {
    currentPath = "/contacts";
  } else if (matchPath("/companies/*", location.pathname)) {
    currentPath = "/companies";
  } else if (matchPath("/deals/*", location.pathname)) {
    currentPath = "/deals";
  } else if (
    matchPath("/metas/*", location.pathname) ||
    matchPath("/metas", location.pathname)
  ) {
    currentPath = "/metas";
  } else if (matchPath("/agenda/*", location.pathname)) {
    currentPath = "/agenda";
  } else if (matchPath("/sales/*", location.pathname)) {
    currentPath = "/sales";
  } else if (matchPath("/parametrizacao/*", location.pathname)) {
    currentPath = "/parametrizacao";
  } else if (matchPath("/api-playground", location.pathname)) {
    currentPath = "/api-playground";
  } else if (
    matchPath("/api-docs", location.pathname) ||
    matchPath("/api-docs/*", location.pathname)
  ) {
    currentPath = "/api-docs";
  } else if (
    matchPath("/settings/*", location.pathname) ||
    matchPath("/settings", location.pathname)
  ) {
    currentPath = "/settings";
  } else if (
    matchPath("/automacoes/*", location.pathname) ||
    matchPath("/automacoes", location.pathname) ||
    matchPath("/automations/*", location.pathname) ||
    matchPath("/automations", location.pathname)
  ) {
    currentPath = "/automacoes";
  } else if (matchPath("/admin/crms", location.pathname)) {
    currentPath = "/admin/crms";
  } else {
    currentPath = false;
  }

  return (
    <aside
      className={cn(
        "h-screen sticky top-0 flex flex-col justify-between border-r border-border/80 bg-card/80 backdrop-blur-md z-30 flex-shrink-0 transition-all duration-300 ease-in-out",
        isCollapsed ? "w-16" : "w-60 xl:w-64",
      )}
    >
      <div className="flex flex-col flex-1 min-h-0">
        {/* Brand / Logo & Notifications */}
        {isCollapsed ? (
          <div className="h-16 px-2 flex items-center justify-center border-b border-border/60">
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={toggleSidebar}
                    className="flex items-center justify-center p-1.5 rounded-lg hover:bg-muted text-foreground transition-colors cursor-pointer"
                    aria-label="Expandir menu lateral"
                  >
                    <img
                      className="[.light_&]:hidden h-8 w-8 object-contain shrink-0"
                      src={darkModeLogo}
                      alt={title}
                    />
                    <img
                      className="[.dark_&]:hidden h-8 w-8 object-contain shrink-0"
                      src={lightModeLogo}
                      alt={title}
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="right"
                  className="text-xs font-medium z-50"
                >
                  Expandir menu lateral
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        ) : (
          <div className="h-16 px-4 flex items-center justify-between border-b border-border/60">
            <Link
              to="/"
              className="flex items-center gap-2 text-foreground no-underline group min-w-0"
            >
              <img
                className="[.light_&]:hidden h-8 w-8 object-contain shrink-0"
                src={darkModeLogo}
                alt={title}
              />
              <img
                className="[.dark_&]:hidden h-8 w-8 object-contain shrink-0"
                src={lightModeLogo}
                alt={title}
              />
              <span className="text-lg font-bold tracking-tight group-hover:text-primary transition-colors truncate">
                {title}
              </span>
            </Link>
            <div className="flex items-center gap-0.5">
              <NotificationsBell />
            </div>
          </div>
        )}

        {/* Notificações em modo recolhido */}
        {isCollapsed && (
          <div className="flex justify-center pt-2 pb-1">
            <NotificationsBell />
          </div>
        )}

        {/* Navigation Items */}
        <div
          className={cn(
            "flex-1 overflow-y-auto flex flex-col gap-4",
            isCollapsed ? "px-2 py-2" : "px-3 py-3",
          )}
        >
          {/* Main nav */}
          <div>
            {isCollapsed ? (
              <div className="my-1.5 border-t border-border/40 mx-1" />
            ) : (
              <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider px-3 mb-2">
                Menu Principal
              </div>
            )}
            <nav className="flex flex-col gap-1">
              <SidebarTab
                icon={LayoutDashboard}
                label={translate("ra.page.dashboard", { _: "Dashboard" })}
                to="/"
                isActive={currentPath === "/"}
                isCollapsed={isCollapsed}
              />
              <SidebarTab
                icon={Contact}
                label={translate("resources.contacts.name", {
                  smart_count: 2,
                  _: "Contatos",
                })}
                to="/contacts"
                isActive={currentPath === "/contacts"}
                isCollapsed={isCollapsed}
              />
              <SidebarTab
                icon={Building2}
                label={translate("resources.companies.name", {
                  smart_count: 2,
                  _: "Empresas",
                })}
                to="/companies"
                isActive={currentPath === "/companies"}
                isCollapsed={isCollapsed}
              />
              <SidebarTab
                icon={TrendingUp}
                label={translate("resources.deals.name", {
                  smart_count: 2,
                  _: "Negócios",
                })}
                to="/deals"
                isActive={currentPath === "/deals"}
                isCollapsed={isCollapsed}
              />
              <SidebarTab
                icon={Target}
                label="Metas"
                to="/metas"
                isActive={currentPath === "/metas"}
                isCollapsed={isCollapsed}
              />
              <SidebarTab
                icon={Calendar}
                label="Agenda"
                to="/agenda"
                isActive={currentPath === "/agenda"}
                isCollapsed={isCollapsed}
              />
            </nav>
          </div>

          {/* Config / Admin nav */}
          <div>
            {isCollapsed ? (
              <div className="my-1.5 border-t border-border/40 mx-1" />
            ) : (
              <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider px-3 mb-2">
                Configurações
              </div>
            )}
            <nav className="flex flex-col gap-1">
              <CanAccess resource="configuration" action="edit">
                <SidebarTab
                  icon={Settings}
                  label="Geral"
                  to="/settings"
                  isActive={currentPath === "/settings"}
                  isCollapsed={isCollapsed}
                />
              </CanAccess>
              <CanAccess resource="sales" action="list">
                <SidebarTab
                  icon={Users}
                  label="Time"
                  to="/sales"
                  isActive={currentPath === "/sales"}
                  isCollapsed={isCollapsed}
                />
              </CanAccess>
              <CanAccess resource="parametrizacao" action="list">
                <SidebarTab
                  icon={Sliders}
                  label="Parametrização"
                  to="/parametrizacao"
                  isActive={currentPath === "/parametrizacao"}
                  isCollapsed={isCollapsed}
                />
              </CanAccess>
              <CanAccess resource="configuration" action="list">
                <SidebarTab
                  icon={Workflow}
                  label="Automações"
                  to="/automacoes"
                  isActive={currentPath === "/automacoes"}
                  isCollapsed={isCollapsed}
                />
              </CanAccess>
              <CanAccess resource="api" action="list">
                <SidebarTab
                  icon={Zap}
                  label="API Playground"
                  to="/api-playground"
                  isActive={currentPath === "/api-playground"}
                  isCollapsed={isCollapsed}
                />
              </CanAccess>
              <CanAccess resource="api" action="list">
                <SidebarTab
                  icon={BookOpen}
                  label="Documentação API"
                  to="/api-docs"
                  isActive={currentPath === "/api-docs"}
                  isCollapsed={isCollapsed}
                />
              </CanAccess>
            </nav>
          </div>
        </div>
      </div>

      {/* Footer Actions: User, Theme, Refresh, VoIP */}
      <div
        className={cn(
          "border-t border-border/60 bg-muted/20 transition-all",
          isCollapsed
            ? "p-2 flex flex-col items-center gap-2"
            : "p-3 flex items-center justify-between gap-1",
        )}
      >
        {!isCollapsed ? (
          <>
            <div className="flex items-center gap-1">
              <ThemeModeToggle />
              <RefreshButton />
              <VoipHeaderButton />
            </div>

            <UserMenu>
              <ProfileMenu />
              <VoipMenuItem />
              <CanAccess resource="configuration" action="edit">
                <SettingsMenu />
              </CanAccess>
              <ImportFromJsonMenuItem />
              <ChangelogMenuItem />
            </UserMenu>
          </>
        ) : (
          <>
            <div className="flex flex-col items-center gap-1.5">
              <ThemeModeToggle />
              <VoipHeaderButton />
            </div>

            <UserMenu>
              <ProfileMenu />
              <VoipMenuItem />
              <CanAccess resource="configuration" action="edit">
                <SettingsMenu />
              </CanAccess>
              <ImportFromJsonMenuItem />
              <ChangelogMenuItem />
            </UserMenu>
          </>
        )}
      </div>
    </aside>
  );
};

const SidebarTab = ({
  icon: Icon,
  label,
  to,
  isActive,
  isCollapsed,
}: {
  icon: React.ElementType;
  label: string;
  to: string;
  isActive: boolean;
  isCollapsed?: boolean;
}) => {
  const content = (
    <Link
      to={to}
      className={cn(
        "flex items-center rounded-lg text-xs font-medium transition-all",
        isCollapsed ? "w-10 h-10 justify-center mx-auto" : "gap-3 px-3 py-2.5",
        isActive
          ? "bg-primary text-primary-foreground font-semibold shadow-xs"
          : "text-muted-foreground hover:text-foreground hover:bg-muted",
      )}
    >
      <Icon className="w-4 h-4 flex-shrink-0" />
      {!isCollapsed && <span className="truncate">{label}</span>}
    </Link>
  );

  if (isCollapsed) {
    return (
      <TooltipProvider delayDuration={50}>
        <Tooltip>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent side="right" className="text-xs font-medium z-50">
            {label}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return content;
};

const ProfileMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ProfileMenu> must be used inside <UserMenu?");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/profile" className="flex items-center gap-2">
        <User />
        {translate("crm.profile.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const SettingsMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<SettingsMenu> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/settings" className="flex items-center gap-2">
        <Settings />
        {translate("crm.settings.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const ImportFromJsonMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ImportFromJsonMenuItem> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={ImportDataPage.path} className="flex items-center gap-2">
        <Import />
        {translate("crm.header.import_data")}
      </Link>
    </DropdownMenuItem>
  );
};

const ChangelogMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ChangelogMenuItem> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={ChangelogPage.path} className="flex items-center gap-2">
        <FileText />
        {translate("crm.changelog.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const VoipHeaderButton = () => {
  const { isVoipEnabled } = useCRM();
  const { isConnected, ramal, openLoginModal } = useVoip();

  if (!isVoipEnabled) {
    return null;
  }

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            size="icon"
            variant="ghost"
            className={`w-7 h-7 rounded-full p-0 transition-colors ${
              isConnected
                ? "text-emerald-500 hover:text-emerald-600 hover:bg-emerald-500/15"
                : "text-muted-foreground hover:text-foreground hover:bg-muted"
            }`}
            onClick={openLoginModal}
            aria-label="Status da Telefonia VoIP"
          >
            <PhoneCall className="w-3.5 h-3.5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-[11px]">
          {isConnected
            ? `VoIP Ativo (Ramal ${ramal})`
            : "Conectar Telefonia VoIP (API4COM)"}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const VoipMenuItem = () => {
  const { isVoipEnabled } = useCRM();
  const { isConnected, ramal, openLoginModal, logout } = useVoip();
  const userMenuContext = useUserMenu();

  if (!isVoipEnabled) {
    return null;
  }

  if (isConnected) {
    return (
      <>
        <DropdownMenuItem
          onClick={() => {
            userMenuContext?.onClose();
            openLoginModal();
          }}
          className="cursor-pointer gap-2"
        >
          <PhoneCall className="w-4 h-4 text-emerald-500" />
          <span>VoIP: Ramal {ramal}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={async () => {
            userMenuContext?.onClose();
            await logout();
          }}
          className="cursor-pointer gap-2 text-destructive focus:text-destructive"
        >
          <LogOut className="w-4 h-4" />
          <span>Sair do VoIP (Logout)</span>
        </DropdownMenuItem>
      </>
    );
  }

  return (
    <DropdownMenuItem
      onClick={() => {
        userMenuContext?.onClose();
        openLoginModal();
      }}
      className="cursor-pointer gap-2"
    >
      <PhoneCall className="w-4 h-4" />
      <span>Conectar VoIP (API4COM)</span>
    </DropdownMenuItem>
  );
};

export default Header;
